import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import SiteNav from "@/components/nearcore/SiteNav";
import Footer from "@/components/nearcore/Footer";
import QuoteModal from "@/components/nearcore/QuoteModal";
import HomePage from "@/pages/HomePage";

const HomeV2Page = lazy(() => import("@/pages/HomeV2Page"));
const HomeV3Page = lazy(() => import("@/pages/HomeV3Page"));
const TalentPage = lazy(() => import("@/pages/TalentPage"));
const JoinPage = lazy(() => import("@/pages/JoinPage"));
const ResourcesPage = lazy(() => import("@/pages/ResourcesPage"));
const ArticlesPage = lazy(() => import("@/pages/ArticlesPage"));
const GlossaryPage = lazy(() => import("@/pages/GlossaryPage"));
const PostPage = lazy(() => import("@/pages/PostPage"));
const FaqPage = lazy(() => import("@/pages/FaqPage"));
const RolePage = lazy(() => import("@/pages/RolePage"));
const TechnologiesPage = lazy(() => import("@/pages/TechnologiesPage"));
const TechnologyPage = lazy(() => import("@/pages/TechnologyPage"));
const CalculatorPage = lazy(() => import("@/pages/CalculatorPage"));
const ComparePage = lazy(() => import("@/pages/ComparePage"));
const VsTraditionalPage = lazy(() => import("@/pages/VsTraditionalPage"));
const BoutiquePage = lazy(() => import("@/pages/BoutiquePage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

function ScrollManager({ lenisRef }: { lenisRef: React.RefObject<Lenis | null> }) {
  const { pathname } = useLocation();
  useEffect(() => {
    lenisRef.current?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname, lenisRef]);
  return null;
}

export default function App() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const lenisRef = useRef<Lenis | null>(null);
  const openQuote = () => setQuoteOpen(true);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisRef.current = lenis;
    let rafId: number;
    const raf = (time: number) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    const onClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="#"]');
      if (!anchor) return;
      const hash = anchor.getAttribute("href");
      if (hash && hash.length > 1 && document.querySelector(hash)) {
        e.preventDefault();
        lenis.scrollTo(hash, { offset: -24, duration: 1.4 });
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("click", onClick);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-white font-body text-brand-slate antialiased">
        <ScrollManager lenisRef={lenisRef} />
        <SiteNav onQuote={openQuote} />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage onQuote={openQuote} />} />
            <Route path="/home-v2" element={<HomeV2Page onQuote={openQuote} />} />
            <Route path="/home-v3" element={<HomeV3Page onQuote={openQuote} />} />
            <Route path="/talent" element={<TalentPage onQuote={openQuote} />} />
            <Route path="/join" element={<JoinPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/glossary" element={<GlossaryPage />} />
            <Route path="/resources/:slug" element={<PostPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/roles/:slug" element={<RolePage onQuote={openQuote} />} />
            <Route path="/technologies" element={<TechnologiesPage onQuote={openQuote} />} />
            <Route path="/technologies/:slug" element={<TechnologyPage onQuote={openQuote} />} />
            <Route path="/calculator" element={<CalculatorPage onQuote={openQuote} />} />
            <Route path="/compare" element={<ComparePage onQuote={openQuote} />} />
            <Route path="/compare/nearshore-staff-augmentation" element={<VsTraditionalPage onQuote={openQuote} />} />
            <Route path="/compare/boutique-staffing" element={<BoutiquePage onQuote={openQuote} />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        <Footer />
        <QuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
      </div>
    </BrowserRouter>
  );
}
